import { pick } from 'lodash';
import * as z from 'zod';

import { EscrowAnalysisValidator } from '@willow/types-iso';

import { Sanitizer } from '../shared/sanitizeRequest';

// If this function returns false, the refinement must exit early.
const getParsedFields = <T, K extends keyof EscrowAnalysisValidator>(data: T, fields: K[]) => {
  const zodPickArgs: z.util.Exactly<{ [_ in K]?: boolean }, { [_ in K]?: boolean }> = {};
  for (const field of fields) {
    (zodPickArgs as any)[field] = true;
  }

  const partial = pick(data, fields);

  const sanitized = Sanitizer.safeParse(partial);

  if (!sanitized.success) {
    return false;
  }

  const parsed = EscrowAnalysisValidator.pick(zodPickArgs).safeParse(sanitized.data);

  if (!parsed.success) {
    return false;
  }

  return parsed.data;
};

const escrowPoliciesHaveAmounts = (data: EscrowAnalysisValidator, ctx: z.RefinementCtx) => {
  const parsed = getParsedFields(data, ['activeEscrowedItems']);

  if (!parsed) return;

  if (parsed.activeEscrowedItems.some((e) => e.amount === 0)) {
    ctx.addIssue({
      code: 'custom',
      message: 'Some escrow lines are missing policy amount',
      path: ['activeEscrowedItems'],
    });
  }
};

const missingEscrowSettings = (data: EscrowAnalysisValidator, ctx: z.RefinementCtx) => {
  const parsed = getParsedFields(data, ['activeEscrowedItems']);

  if (!parsed) return;

  // Escrow payment is > 0 and there are no active escrow items. User must add escrow settings.
  if (!parsed.activeEscrowedItems.length) {
    ctx.addIssue({
      code: 'custom',
      message: 'Missing escrow data',
      path: ['activeEscrowedItems'],
    });
  }
};

const noFulfilledStatementsOnOrAfterEffectiveDate = (data: EscrowAnalysisValidator, ctx: z.RefinementCtx) => {
  const parsed = getParsedFields(data, ['earliestEffectiveDateAfterFulfilledStatements']);
  if (!parsed) return;
  if (parsed.earliestEffectiveDateAfterFulfilledStatements) {
    ctx.addIssue({
      code: 'custom',
      message: `Statements due on or after the effective date have already been paid; rerun analysis with an effective date of ${parsed.earliestEffectiveDateAfterFulfilledStatements}`,
      path: ['effectiveDate'],
    });
  }
};

export const EscrowAnalysisCrossFieldParser = z
  .any()
  .superRefine(escrowPoliciesHaveAmounts)
  .superRefine(missingEscrowSettings)
  .superRefine(noFulfilledStatementsOnOrAfterEffectiveDate);
